import { showError, showSuccess } from '@calendly/alert';
import { useFlag } from '@calendly/feature-check-react';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconBan } from '@calendly/ui/components/glyphs/IconBan';
import { IconCircleX } from '@calendly/ui/components/glyphs/IconCircleX';
import { IconDotsVertical } from '@calendly/ui/components/glyphs/IconDotsVertical';
import { IconMail } from '@calendly/ui/components/glyphs/IconMail';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { IconRotateClockwise } from '@calendly/ui/components/glyphs/IconRotateClockwise';
import { IconRotateClockwise2 } from '@calendly/ui/components/glyphs/IconRotateClockwise2';
import { IconUserSquareRounded } from '@calendly/ui/components/glyphs/IconUserSquareRounded';
import { IconButton } from '@calendly/ui/components/icon-button';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { DeviceTypeProvider, useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { useViewportSize } from '@calendly/ui/hooks/viewport-size-context';
import { zIndexDropdown } from '@calendly/ui/z-index';
import classNames from 'classnames';
import { forwardRef, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BlockModal, UnblockModal, BlockExpiredModal } from 'components/meetings/block_modals';
import NoShowModal from 'components/scheduled_events/modals/no_show';
import { HostReschedule } from 'features/host_reschedule';
import { CRM_SHAREABLE } from 'features/mfe_shareable';
import { useRightRail } from 'features/right_rail';
import { SendPopup, useSendEmailsMutation, FlowKind, closeSendPopup } from 'features/send_from_calendly';
import { CONTACT_INTERACTION_LOCATIONS } from 'hooks/analytics_events/constants';
import { useOpenEmailComposerFromHost } from 'hooks/oap_web/use_open_email_composer_from_host';
import { isInviteeOlderThan90Days } from './block_expiry_utils';
import { CancelInviteeModal } from './cancel_invitee_modal';
import { ItemContents } from './item_contents';
import { useNoShow } from './use_no_show';

/*
  SA-4336: Long DE/FR/ES primary labels used to push the kebab past the invitee
  card's overflow clip. Keep the kebab in a non-shrinking right flex slot;
  primary actions wrap onto the next line with full labels when they do not fit.
*/
const inviteeActionsLayoutCss = "i1kvepni";
const primaryInviteeActionButtonCss = "p18zasxk";
const primaryActionsRowCss = "p4tel3d";
const primaryActionsRowThreeItemsCss = "pe7m3r7";
const inviteeActionsKebabHostCss = "icb08g0";

/*
  Match header MoreActions: CUI DropdownMenuContent on phone portals via FixedContainer
  (default z-index 15) under MobileSidePanel (250). Use zIndexDropdown (300): above the
  side panel, below zIndexModal (500) so Edit/Remove/Reschedule surfaces stay on top.
*/
export const INVITEE_ACTIONS_MENU_STACKING_Z_INDEX = zIndexDropdown;
const inviteeActionsMenuStackingCss = "ibwa8ts";

/*
  Shell mobile overlay uses viewportSmall (≤895px). CUI DropdownMenu phone sheet uses
  useDeviceType('isPhone') (≤800px). In the 801–895 band — and whenever isPhone fails to
  match while the shell is already mobile — force the phone sheet so invitee matches header.
*/
const FORCE_PHONE_MENU_DEVICE_TYPE = {
  breakpoints: {
    isPhone: {
      minWidth: 0,
      maxWidth: 10000
    }
  }
};
const PrimaryInviteeActionButton = ({
  icon,
  label,
  onClick
}) => <MinimalButton className={primaryInviteeActionButtonCss} leadingIcon={icon} onClick={onClick} size="small" title={label}>
    {label}
  </MinimalButton>;
const PrimaryActionsRow = forwardRef((props, ref) => {
  const {
    as: AsComponent = 'div',
    ...restProps
  } = props;
  const Component = AsComponent;
  return <Component ref={ref} {...restProps} className={classNames(primaryActionsRowCss, props.className)} />;
});

/*
  HostReschedule must stay mounted outside DropdownMenuContent. Closing the menu unmounts
  content; if HostReschedule lived there, its Redux-driven reschedule popup unmounted with it
  (modal appeared only on the next kebab open, and later kebab clicks could re-open the modal).
*/
const RescheduleRegistrationContext = createContext(null);
const RegisterRescheduleHandler = ({
  onClick,
  rescheduleOnClickRef,
  onHandlerReadyChange
}) => {
  useEffect(() => {
    rescheduleOnClickRef.current = onClick;
    onHandlerReadyChange(true);
    return () => {
      rescheduleOnClickRef.current = null;
      onHandlerReadyChange(false);
    };
  }, [onClick, onHandlerReadyChange, rescheduleOnClickRef]);
  return null;
};

/** Passed to HostReschedule as `Button` (stable ref for eslint react/no-unstable-nested-components). */
const HostRescheduleRegisterButton = ({
  onClick
}) => {
  const registration = useContext(RescheduleRegistrationContext);
  if (!registration) return null;
  return <RegisterRescheduleHandler onClick={onClick} rescheduleOnClickRef={registration.rescheduleOnClickRef} onHandlerReadyChange={registration.onHandlerReadyChange} />;
};
export const InviteeActions = ({
  event,
  invitee,
  canReschedule,
  canCancel,
  canBlock,
  canUnblock,
  openEditEmailModal,
  canEditEmail,
  hideRescheduleFromMenu = false,
  hideRemoveFromMenu = false,
  hideBlockFromMenu = false,
  hideUnblockFromMenu = false,
  showPrimaryActions = false,
  resolved,
  canMarkNoShow,
  onMarkAsNoShow,
  sendAnalytics,
  /** When true, kebab menu is omitted entirely (e.g. external meeting invitees). */
  hideKebabMenu = false
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    close
  } = useRightRail();
  const [sendEmails] = useSendEmailsMutation();
  const isNewEmailComposerEnabled = useFlag('crm_new_email_composer');
  const openHostEmailComposer = useOpenEmailComposerFromHost();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEmailPopupOpen, openEmailPopup, closeEmailPopup] = useBooleanState(false);
  const [isCancelInviteeModalOpen, openCancelInviteeModal, closeCancelInviteeModal] = useBooleanState(false);
  const [isBlockModalOpen, openBlockModal, closeBlockModal] = useBooleanState(false);
  const [isUnblockModalOpen, openUnblockModal, closeUnblockModal] = useBooleanState(false);
  const [isBlockExpiredModalOpen, openBlockExpiredModal, closeBlockExpiredModal] = useBooleanState(false);
  const rescheduleOnClickRef = useRef(null);
  const [isRescheduleHandlerReady, setIsRescheduleHandlerReady] = useState(false);
  const isPhone = useDeviceType('isPhone');
  const {
    isSmallViewport
  } = useViewportSize();
  const usePhoneMenuSheet = isPhone || isSmallViewport;
  const phoneMenuDeviceType = useMemo(() => usePhoneMenuSheet ? FORCE_PHONE_MENU_DEVICE_TYPE : {
    breakpoints: {}
  }, [usePhoneMenuSheet]);
  const rescheduleRegistration = useMemo(() => ({
    rescheduleOnClickRef,
    onHandlerReadyChange: setIsRescheduleHandlerReady
  }), []);
  const {
    isModalOpen: isNoShowModalOpen,
    closeModal: closeNoShowModal,
    handleMarkNoShow: handleMarkNoShowConfirm,
    handleUndoNoShow,
    handleNoShowClick
  } = useNoShow({
    invitee,
    event,
    onMarkAsNoShow: onMarkAsNoShow ?? (() => {})
  });
  const isBlocked = invitee.blockedByHost;
  const isOlderThan90Days = isInviteeOlderThan90Days(invitee.createdAt);
  const shouldMountHostReschedule = !isBlocked && canReschedule && !hideRescheduleFromMenu;
  const closeDropdown = () => setIsDropdownOpen(false);
  const handleToggleDropdown = () => {
    setIsDropdownOpen(open => !open);
  };
  const handleReschedule = () => {
    closeDropdown();
    rescheduleOnClickRef.current?.();
  };
  const handleEditEmail = () => {
    closeDropdown();
    openEditEmailModal();
  };
  const handleRemoveInvitee = () => {
    closeDropdown();
    openCancelInviteeModal();
  };
  const handleBlockClick = () => {
    closeDropdown();
    if (isOlderThan90Days) {
      openBlockExpiredModal();
    } else {
      openBlockModal();
    }
  };
  const handleUnblockClick = () => {
    closeDropdown();
    openUnblockModal();
  };
  const handleViewFullProfile = () => {
    const profileRouteId = resolved?.contactId ?? resolved?.contactUuid;
    if (sendAnalytics && resolved) {
      sendAnalytics('contact_details_view', resolved.contactUuid ?? resolved.contactId);
    }
    close();
    if (profileRouteId) history.push(`/app/contacts/${profileRouteId}`);
  };
  const handleEmailClick = () => {
    dispatch(closeSendPopup());
    const contactRecordId = resolved?.contactId ?? resolved?.contactUuid;
    if (isNewEmailComposerEnabled && contactRecordId) {
      openHostEmailComposer({
        recipientIds: [contactRecordId],
        sendViaCalendlySource: CONTACT_INTERACTION_LOCATIONS.MEETINGS_PAGE_RIGHT_RAIL
      });
    } else {
      openEmailPopup();
    }
  };
  const handleEmailSend = async values => {
    if (!resolved) return;
    const {
      error
    } = await sendEmails({
      sendWith: values.sendWith ? `${values.sendWith.type}_${values.sendWith.accountUuid}` : null,
      to: values.to[0]?.email,
      cc: values.cc.map(({
        email
      }) => email),
      bcc: values.bcc.map(({
        email
      }) => email),
      body: values.body,
      subject: values.subject?.trim() || I18n.t('publisher.send_from_calendly.fields.subject.no_subject'),
      remindAt: values.isReminderActive ? values.remindInDays : undefined,
      inviteeUuid: invitee?.uuid,
      contactUuid: resolved.contactUuid ?? resolved.contactId,
      eventTypeUuid: null,
      kind: FlowKind.contact,
      rescheduleViaShareAvailability: false,
      includeCancellationPolicy: false
    });
    if (error) {
      dispatch(showError(I18n.t('publisher.send_from_calendly.messages.error')));
      return;
    }
    dispatch(showSuccess(I18n.t('publisher.send_from_calendly.messages.success')));
    closeEmailPopup();
  };
  const emailInitialValues = resolved ? {
    sendWith: null,
    to: [{
      id: resolved.contactId ?? '',
      email: resolved.email ?? '',
      name: resolved.name ?? ''
    }],
    cc: [],
    showCc: false,
    bcc: [],
    showBcc: false,
    subject: '',
    body: `<p>${I18n.t('publisher.mailer.greeting_hello', {
      name: resolved.name ?? ''
    })}</p>`,
    timeSlotSuggestions: null,
    remindInDays: 3,
    kind: FlowKind.contact,
    isReminderActive: false
  } : null;
  const hasContactForPrimaryActions = Boolean(resolved?.contactUuid || resolved?.contactId);
  const primaryActionButtonCount = (canMarkNoShow && !invitee.blockedByHost ? 1 : 0) + (hasContactForPrimaryActions ? 2 : 0);
  const hasKebabItems = !hideKebabMenu && (isBlocked ? canUnblock && !hideUnblockFromMenu : canReschedule && !hideRescheduleFromMenu || canCancel && !hideRemoveFromMenu || canEditEmail || canBlock && !hideBlockFromMenu);
  const rescheduleHref = `/reschedulings/${invitee.uuid}?&return_to=${window.location.pathname}`;
  const rescheduleMenuLink = <DropdownMenuItem href={rescheduleHref}>
      <ItemContents>
        <UiIcon icon={IconRotateClockwise} size="small" spacingRight="4" />
        {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
      </ItemContents>
    </DropdownMenuItem>;
  const kebab = <div className={inviteeActionsKebabHostCss}>
      {/*
        Phone / shell-small: force CUI phone sheet (omit placement/offset like profile menu).
        DeviceTypeProvider keeps DropdownMenu on the sheet path when shell is ≤895 but
        useDeviceType('isPhone') alone would still be false (≤800).
       */}
      <DeviceTypeProvider value={phoneMenuDeviceType}>
        <DropdownMenu isOpen={isDropdownOpen} onClose={closeDropdown}>
          <DropdownMenuTrigger>
            <IconButton size="small" onClick={handleToggleDropdown} title={I18n.t('publisher.meetings.invitees.invitee_actions')} aria-label={I18n.t('publisher.meetings.invitees.invitee_actions')} aria-expanded={isDropdownOpen} aria-haspopup="menu">
              <UiIcon icon={IconDotsVertical} size="small" />
            </IconButton>
          </DropdownMenuTrigger>
          {isDropdownOpen && <DropdownMenuContent className={inviteeActionsMenuStackingCss} data-testid="invitee-actions-menu" {...usePhoneMenuSheet ? {} : {
          placement: 'bottom end'
        }}>
              {!isBlocked && canReschedule && !hideRescheduleFromMenu && (isRescheduleHandlerReady ? <DropdownMenuItem onSelect={handleReschedule}>
                    <ItemContents>
                      <UiIcon icon={IconRotateClockwise} size="small" spacingRight="4" />
                      {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
                    </ItemContents>
                  </DropdownMenuItem> : rescheduleMenuLink)}
              {!isBlocked && canEditEmail && <DropdownMenuItem onSelect={handleEditEmail}>
                  <ItemContents>
                    <UiIcon icon={IconPencil} size="small" spacingRight="4" />
                    {I18n.t('publisher.meetings.invitees.edit_invitee_email')}
                  </ItemContents>
                </DropdownMenuItem>}
              {!isBlocked && canCancel && !hideRemoveFromMenu && <DropdownMenuItem onSelect={handleRemoveInvitee}>
                  <ItemContents>
                    <UiIcon icon={IconCircleX} size="small" spacingRight="4" />
                    {I18n.t('publisher.meetings.invitees.remove_invitee')}
                  </ItemContents>
                </DropdownMenuItem>}
              {canBlock && !hideBlockFromMenu && <DropdownMenuItem onSelect={handleBlockClick}>
                  <ItemContents>
                    <UiIcon icon={IconBan} size="small" spacingRight="4" />
                    {event.past ? I18n.t('publisher.block_invitee.action.block_attendee') : I18n.t('publisher.block_invitee.action.block_invitee')}
                  </ItemContents>
                </DropdownMenuItem>}
              {canUnblock && !hideUnblockFromMenu && <DropdownMenuItem onSelect={handleUnblockClick} disabled={isOlderThan90Days}>
                  <ItemContents>
                    <UiIcon icon={IconRotateClockwise2} size="small" spacingRight="4" />
                    {event.past ? I18n.t('publisher.block_invitee.action.unblock_attendee') : I18n.t('publisher.block_invitee.action.unblock_invitee')}
                  </ItemContents>
                </DropdownMenuItem>}
            </DropdownMenuContent>}
        </DropdownMenu>
      </DeviceTypeProvider>
    </div>;
  const showActionsLayout = showPrimaryActions || hasKebabItems;
  return <>
      {showActionsLayout && <div className={inviteeActionsLayoutCss} data-testid="invitee-actions-layout">
          {showPrimaryActions && <PrimaryActionsRow data-testid="attendee-actions" className={primaryActionButtonCount === 3 ? primaryActionsRowThreeItemsCss : undefined}>
              {canMarkNoShow && !invitee.blockedByHost && (invitee.noShow ? <PrimaryInviteeActionButton icon={IconRotateClockwise2} label={I18n.t('publisher.meetings.invitees.undo_no_show')} onClick={handleUndoNoShow} /> : <PrimaryInviteeActionButton icon={IconBan} label={I18n.t('publisher.meetings.invitees.mark_no_show')} onClick={handleNoShowClick} />)}
              {hasContactForPrimaryActions && <PrimaryInviteeActionButton icon={IconMail} label={I18n.t('publisher.meetings.invitees.email')} onClick={handleEmailClick} />}
              {hasContactForPrimaryActions && <PrimaryInviteeActionButton icon={IconUserSquareRounded} label={I18n.t('publisher.meetings.invitees.view_full_profile')} onClick={handleViewFullProfile} />}
            </PrimaryActionsRow>}
          {hasKebabItems && kebab}
        </div>}
      {showPrimaryActions && isNoShowModalOpen && <NoShowModal inviteeName={invitee.name} onConfirm={handleMarkNoShowConfirm} onClose={closeNoShowModal} />}
      {showPrimaryActions && isEmailPopupOpen && emailInitialValues && <SendPopup initialFormValues={emailInitialValues} onSend={handleEmailSend} onCancel={closeEmailPopup} source={CRM_SHAREABLE} viewConfig={{
      isSubjectAtTop: true,
      isReminderFieldVisible: false,
      showConnectEmailContainer: true,
      showShareSubHeader: false
    }} />}
      {shouldMountHostReschedule && <RescheduleRegistrationContext.Provider value={rescheduleRegistration}>
          <HostReschedule event={event} invitee={invitee} isInviteeOnlyReschedule sendAnalytics={sendAnalytics} Button={HostRescheduleRegisterButton} />
        </RescheduleRegistrationContext.Provider>}
      {isCancelInviteeModalOpen && <CancelInviteeModal invitee={invitee} onClose={closeCancelInviteeModal} />}
      {isBlockModalOpen && <BlockModal event={event} invitee={invitee} onClose={closeBlockModal} />}
      {isUnblockModalOpen && <UnblockModal event={event} invitee={invitee} onClose={closeUnblockModal} />}
      {isBlockExpiredModalOpen && <BlockExpiredModal inviteeName={invitee.name} isPast={event.past} onClose={closeBlockExpiredModal} />}
    </>;
};

require("./invitee_actions.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitee_actions.js");